/* eslint-disable no-redeclare */

import type { Atom } from "../types/Atom"
import type { Sequence } from "../types/Sequence"
import { IsArray } from "../checks/IsArray"
import { Composition } from "./Composition"
import { Identity } from "./Identity"
import { Zip } from "./Zip"

/**
 * Sorts sequence by transformer function.
 *
 * Sorts by single or multiple criteria. Transformer returns Atom or Atom[] for comparison.
 *
 * @param sequence - Iterable to sort
 * @param transformer - Function mapping elements to comparable value(s) (default: Identity)
 * @returns Sorted array
 *
 * @example
 * ```ts
 * import { SortBy } from "@pdfking/tfunc"
 *
 * SortBy([4, 3, 2, 1]) // [1, 2, 3, 4]
 * SortBy([{ a: 2 }, { a: 1 }], x => x.a) // [{ a: 1 }, { a: 2 }]
 * SortBy([{ a: 1, b: 2 }, { a: 1, b: 1 }], x => [x.a, x.b])
 * ```
 */
export function SortBy<T extends Atom | Atom[]>(sequence: Sequence<T>): T[]
export function SortBy<T, K extends Atom | Atom[]>(
  sequence: Sequence<T>,
  transformer: (item: T) => K
): T[]
export function SortBy<T, K extends Atom | Atom[]>(
  sequence: Sequence<T>,
  transformer?: (item: T) => K
): T[] {
  const extract = Composition(
    transformer ?? (Identity as (item: T) => Atom | Atom[]),
    value => (IsArray(value) ? value : [value])
  )

  const compare = (a: T, b: T): number => {
    for (const [v1, v2] of Zip(extract(a), extract(b))) {
      if (v1 !== v2) {
        return v1 > v2 ? 1 : -1
      }
    }
    return 0
  }

  return Array.from(sequence).sort(compare)
}
